import { map } from "lodash";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { SubtitleText } from "@/shared/ui/Typography";
import { Card } from "@/shared/ui/Card";
import { Title } from "@/shared/ui/Title";
import { ProgressIndicator } from "@/shared/widgets/ProgressIndicatorCTA";
import useOnboarding from "@/features/profile/hooks/useOnboarding";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { cn } from "@/shared/utils/shadcn";

export function OnboardingActions() {
  const { t } = useTranslation();
  const { isOnboardingCompleted, taskList } = useOnboarding();
  const navigate = useOrgNavigate();

  const tasksCount = useMemo(
    () => ({
      completed: taskList.filter((task) => task.completed).length,
      total: taskList.length,
    }),
    [taskList]
  );

  if (isOnboardingCompleted) {
    return null;
  }

  return (
    <Card className="bg-grey-100 p-2 border-none">
      <Row className="gap-4 p-2">
        <Icon icon="Flag" color="blue" size="sm" className="mt-1" />
        <Title as="h6" description={t("onboarding.setup_account_description")}>
          {t("onboarding.setup_account_title")}
        </Title>
        <Row className="gap-4 self-start flex-1 justify-end items-center flex-wrap min-w-[100px] hidden sm:flex">
          <ProgressIndicator
            totalCount={tasksCount.total}
            completedCount={tasksCount.completed}
          />
          <SubtitleText>
            {`${tasksCount.completed} of ${tasksCount.total}`}
          </SubtitleText>
        </Row>
      </Row>
      <Column className="gap-4 mt-2">
        {map(taskList, (task) => (
          <Row
            className={cn(
              "border-grey-200 border gap-4 items-center px-4 py-2 bg-white rounded-lg cursor-pointer hover:bg-gray-200/50 group",
              task.completed && "bg-grey-200/50"
            )}
            key={task.id}
            onClick={() => {
              navigate(task.path);
            }}
          >
            <Icon
              icon={task.completed ? "CheckFilled" : "CheckOutline"}
              size="sm"
              color={task.completed ? "green" : "lightGrey"}
            />
            <Column className="flex-1">
              <p
                className={cn(
                  "text-sm font-medium",
                  task.completed && "text-gray-500 line-through"
                )}
              >
                {t(task.title_key)}
              </p>
            </Column>
            <Icon
              icon="Arrow"
              color="lightGrey"
              size="sm"
              className="group-hover:translate-x-1 transition-all duration-300"
            />
          </Row>
        ))}
      </Column>
    </Card>
  );
}
